// OnboardingGuide — Progressive contextual onboarding overlay.
// Shows one nudge at a time over the live UI — no separate wizard page.
// Each nudge appears only once; dismissal is persisted to localStorage.
//
// Nudge order:
//   1. bankroll_setup  — bankrollSettings === null
//   2. module_activate — bankroll set, still no recommendations
//   3. first_rec       — first STRONG_BET recommendation arrives

import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ArrowRight, DollarSign, Zap, Activity } from 'lucide-react';
import { useAppStore } from '../store/useAppStore';
import { useOnboardingStore, type NudgeId } from '../store/useOnboardingStore';

// ── Nudge card ────────────────────────────────────────────────────────────────

interface NudgeProps {
  id: NudgeId;
  icon: React.ElementType;
  iconColor: string;
  title: string;
  body: string;
  actionLabel: string;
  onAction: () => void;
}

function NudgeCard({ id, icon: Icon, iconColor, title, body, actionLabel, onAction }: NudgeProps) {
  const dismissNudge = useOnboardingStore((s) => s.dismissNudge);

  return (
    <motion.div
      key={id}
      initial={{ opacity: 0, y: 16, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 8, scale: 0.97 }}
      transition={{ type: 'spring', stiffness: 380, damping: 32 }}
      className="fixed bottom-24 md:bottom-6 right-4 md:right-6 z-[55] w-72 pointer-events-auto"
    >
      {/* Ambient glow */}
      <div
        className="absolute inset-0 rounded-xl blur-xl opacity-20 pointer-events-none"
        style={{ backgroundColor: iconColor }}
      />

      <div className="relative bg-surface-1 border border-surface-4 rounded-xl shadow-2xl overflow-hidden">
        {/* Accent top bar */}
        <div className="h-0.5 w-full" style={{ backgroundColor: iconColor }} />

        <div className="p-4">
          {/* Header */}
          <div className="flex items-start gap-3">
            <div
              className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center border mt-0.5"
              style={{ backgroundColor: `${iconColor}18`, borderColor: `${iconColor}35` }}
            >
              <Icon className="w-4 h-4" style={{ color: iconColor }} />
            </div>

            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-gray-100 leading-snug">{title}</p>
              <p className="text-[11px] text-gray-500 leading-snug mt-1">{body}</p>
            </div>

            <button
              onClick={() => dismissNudge(id)}
              className="flex-shrink-0 text-gray-600 hover:text-gray-400 transition-colors -mt-0.5 -mr-0.5"
              aria-label="Dismiss"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between mt-3 pt-3 border-t border-surface-4">
            <button
              onClick={() => dismissNudge(id)}
              className="text-[11px] text-gray-600 hover:text-gray-400 transition-colors"
            >
              Skip for now
            </button>

            <button
              onClick={() => { onAction(); dismissNudge(id); }}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-semibold text-white transition-all hover:brightness-110 active:scale-95"
              style={{ backgroundColor: iconColor }}
            >
              {actionLabel}
              <ArrowRight className="w-3 h-3" />
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// ── OnboardingGuide ───────────────────────────────────────────────────────────

export function OnboardingGuide() {
  const navigate = useNavigate();
  const { bankrollSettings, recommendations } = useAppStore();
  const { isDismissed } = useOnboardingStore();

  const bankrollSet = bankrollSettings !== null && (bankrollSettings.currentBankroll ?? 0) > 0;
  const hasRecs = recommendations.some(
    (r) => r.decisionStatus === 'STRONG_BET' || r.decisionStatus === 'QUALIFIED_BET',
  );

  // Determine which nudge to show (only one at a time, in priority order)
  let activeNudge: NudgeId | null = null;

  if (!bankrollSet && !isDismissed('bankroll_setup')) {
    activeNudge = 'bankroll_setup';
  } else if (bankrollSet && !hasRecs && !isDismissed('module_activate')) {
    activeNudge = 'module_activate';
  } else if (hasRecs && !isDismissed('first_rec')) {
    activeNudge = 'first_rec';
  }

  return (
    <AnimatePresence>
      {activeNudge === 'bankroll_setup' && (
        <NudgeCard
          id="bankroll_setup"
          icon={DollarSign}
          iconColor="#d97706"
          title="Set your bankroll to unlock full intelligence"
          body="Kelly sizing, exposure controls, and stake recommendations are all calibrated to your bankroll. Takes 30 seconds."
          actionLabel="Configure Bankroll"
          onAction={() => navigate('/portfolio')}
        />
      )}

      {activeNudge === 'module_activate' && (
        <NudgeCard
          id="module_activate"
          icon={Activity}
          iconColor="#3b82f6"
          title="Activate your first intelligence module"
          body="DiamondEdge (MLB) and CourtEdge (WNBA) are ready. Pick your sport and the 9 agents will start analyzing today's slate."
          actionLabel="Go to DiamondEdge"
          onAction={() => navigate('/baseball/mlb/command-center')}
        />
      )}

      {activeNudge === 'first_rec' && (
        <NudgeCard
          id="first_rec"
          icon={Zap}
          iconColor="#10b981"
          title="Your first recommendation is live"
          body="The agents have analyzed today's slate. View the opportunity stream to see confidence scores, agent consensus, and edge."
          actionLabel="View Opportunities"
          onAction={() => navigate('/pulse/picks')}
        />
      )}
    </AnimatePresence>
  );
}
